import { Classes } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { useAppDispatch } from 'src/ui/app/hooks';
import { Dialog } from 'src/ui/components/Dialog';
import { Loading } from 'src/ui/components/Loading';
import { NotFound } from 'src/ui/components/NotFound';
import { NewConnectionForm } from 'src/ui/containers/dialogs/NewConnectionForm';
import { SelectConnectionDialog } from 'src/ui/containers/dialogs/SelectConnectionDialog';
import { Connection, ConnectionStatus } from 'src/ui/features/connection';
import { queueNotification } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useGetConnectionByIdQuery,
  useGetShareByShortHashQuery,
  useListConnectionsByEndpointQuery,
} from 'src/ui/graph/types';
import { useHealthCheck } from 'src/ui/hooks/diagnostic';
import { components } from 'src/ui/templates/copy';
import { getHostnameFromUrl } from 'src/ui/utils/getHostnameFromUrl';
import { doOrgServiceRedirect, doServiceRedirect } from 'src/ui/utils/window';

type SharePathParams = { hash: string };

const OrgShare: React.VFC<{
  orgDomain: string;
  connectionId: string;
  service: string;
  targetPath: string;
}> = ({ orgDomain, connectionId, service, targetPath }) => {
  const {
    data: connectionData,
    isLoading,
    isError,
  } = useGetConnectionByIdQuery(graphQLClient, {
    id: connectionId,
    org_domain: orgDomain,
  });

  const connection = connectionData?.connection;

  React.useEffect(() => {
    if (!isError && connection) {
      const redirectSubPath = `${service}/#${targetPath}`;
      doOrgServiceRedirect(orgDomain, connection.index, redirectSubPath);
    }
  }, [connection, isError, orgDomain, service, targetPath]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !connection) {
    return (
      <NotFound
        title={components.shareOrgConnectionUnavailable.title}
        description={components.shareOrgConnectionUnavailable.description}
      />
    );
  }

  return null;
};

const EndpointShare: React.VFC<{
  endpoint: string;
  service: string;
  targetPath: string;
}> = ({ endpoint, service, targetPath }) => {
  const [endpointValid, setEndpointValid] = React.useState<boolean | null>(
    null
  );
  const dispatch = useAppDispatch();
  const {
    isLoading: isCheckingEndpoint,
    mutate: checkEndpoint,
  } = useHealthCheck();

  const {
    data: connectionData,
    isLoading: connectionsAreLoading,
  } = useListConnectionsByEndpointQuery(
    graphQLClient,
    { endpoint },
    { enabled: !!endpointValid }
  );

  React.useEffect(() => {
    checkEndpoint(endpoint, {
      onError: (error) => {
        setEndpointValid(false);

        dispatch(
          queueNotification(
            `Could not reach endpoint ${endpoint}: ${error.message}`
          )
        );
      },
      onSuccess: () => setEndpointValid(true),
    });
  }, [checkEndpoint, dispatch, endpoint]);

  if (!endpointValid) {
    if (isCheckingEndpoint || endpointValid === null) {
      return <Loading />;
    }

    return <Redirect to="/" />;
  }

  const redirectSubPath = `${service}/#${targetPath}`;
  const isLoading = connectionsAreLoading;
  const connections = connectionData?.listConnectionsByEndpoint?.filter(
    (conn) => !!conn
  );
  const numConnections = connections?.length || 0;

  if (isLoading) {
    return <Loading />;
  }

  if (connections && numConnections > 1) {
    return (
      <SelectConnectionDialog
        connections={connections as Connection[]}
        redirectSubPath={redirectSubPath}
        isOpen
      />
    );
  }

  const firstConnection = connections?.[0];

  if (firstConnection) {
    doServiceRedirect(firstConnection.index, redirectSubPath);

    return <Loading />;
  }

  const newConnection: Connection = {
    name: getHostnameFromUrl(endpoint),
    username: '',
    endpoint,
    status: ConnectionStatus.PENDING,
  };

  return (
    <Dialog
      icon={IconNames.OFFLINE}
      title="Connect To Stardog"
      className={classNames('sd-dialog-connection')}
      isCloseButtonShown={false}
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      isOpen
    >
      <div className={Classes.DIALOG_BODY}>
        <NewConnectionForm
          connection={newConnection}
          redirectSubPath={redirectSubPath}
        />
      </div>
    </Dialog>
  );
};

export const Share: React.VFC = () => {
  const pathParams: SharePathParams = useParams();
  const dispatch = useAppDispatch();
  const {
    data: shareData,
    isLoading: shareIsLoading,
  } = useGetShareByShortHashQuery(graphQLClient, {
    shortHash: pathParams.hash,
  });

  if (shareIsLoading) {
    return <Loading />;
  }
  if (!shareData?.getShareByShortHash) {
    return (
      <NotFound
        title={components.shareNotFound.title}
        description={components.shareNotFound.description}
      />
    );
  }

  const {
    endpoint,
    org_domain: orgDomain,
    connection_id: connectionId,
    service,
    target_path: targetPath,
  } = shareData.getShareByShortHash;

  if (!service || !targetPath) {
    dispatch(queueNotification('Invalid Share'));

    return <Redirect to="/" />;
  }

  if (orgDomain && connectionId) {
    return (
      <OrgShare
        orgDomain={orgDomain}
        connectionId={connectionId}
        service={service}
        targetPath={targetPath}
      />
    );
  }

  if (endpoint) {
    return (
      <EndpointShare
        endpoint={endpoint}
        service={service}
        targetPath={targetPath}
      />
    );
  }

  dispatch(queueNotification('Invalid Share'));

  return <Redirect to="/" />;
};
